/**
 * SourceOfTruthPage — J1-B
 *
 * Read-only view of all Source-of-Truth knowledge items across the org.
 * Groups items by domain for a tree-like overview.
 *
 * Uses GET /api/v1/governance/source-of-truth which returns
 * RorosKnowledgeItemDTO[] filtered to is_source_of_truth = true.
 *
 * PRD §12.3.4 task 4.
 */

import React, { useMemo } from 'react';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Star, BookOpen } from 'lucide-react';
import { t } from '@/lib/i18n';
import { useRorosSourceOfTruth } from '@/hooks/roros/governance';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosKnowledgeItemDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Domain group component ─────────────────────────────────────────────────

interface DomainGroupProps {
  domain: RorosDomainDTO | undefined;
  items: RorosKnowledgeItemDTO[];
}

function DomainGroup({ domain, items }: DomainGroupProps) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 px-1">
        <span
          className="h-2 w-2 rounded-full bg-primary inline-block flex-shrink-0"
          aria-hidden
        />
        {domain?.name ?? t('Unknown Domain')}
        <span className="text-xs text-muted-foreground font-normal">({items.length})</span>
      </h3>
      <div className="space-y-2 pl-4" role="list" aria-label={domain?.name ?? t('Unknown Domain')}>
        {items.map((item) => (
          <SoTItem key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

// ─── Individual SoT item ─────────────────────────────────────────────────────

interface SoTItemProps {
  item: RorosKnowledgeItemDTO;
}

function SoTItem({ item }: SoTItemProps) {
  return (
    <Card
      className="p-3"
      role="listitem"
      tabIndex={0}
      aria-label={item.title}
    >
      <div className="flex items-start gap-3">
        <Star className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{item.title}</span>
            {item.itemType && (
              <Badge variant="secondary" className="text-xs">
                {t(item.itemType)}
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
          )}
          {item.content && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-3 bg-muted p-2 rounded-md font-mono">
              {/* FW9 PM-dogfood fix: item.content is z.record(z.any()) — an object,
                  not a string. Rendering an object directly as a React child throws
                  "Objects are not valid as a React child" and breaks the SoT page
                  for any tenant whose Knowledge items have non-empty content.
                  Stringify defensively, but only when it's not already a string. */}
              {typeof item.content === 'string'
                ? item.content
                : JSON.stringify(item.content, null, 2)}
            </p>
          )}
          {item.sourceReference && (
            <a
              href={item.sourceReference}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-primary hover:underline mt-1 block truncate"
            >
              {item.sourceReference}
            </a>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export function SourceOfTruthPage() {
  const sotQuery = useRorosSourceOfTruth();
  const domainsQuery = useRorosDomains();

  const domains = (domainsQuery.data ?? []) as RorosDomainDTO[];
  // Backend returns RorosKnowledgeItemDTO[] at runtime (typed as RorosSourceOfTruthDTO)
  const items = (sotQuery.data ?? []) as unknown as RorosKnowledgeItemDTO[];

  // Group by domainId.
  // J7-W4 fix-wave 2 (B7): item.domainId is nullable (system-scope items
  // store NULL). Bucket nulls under the literal 'system' key so they group
  // together in the listing (matches the System Knowledge convention used
  // elsewhere in the UI).
  const grouped = useMemo(() => {
    const map = new Map<string, RorosKnowledgeItemDTO[]>();
    for (const item of items) {
      const key = item.domainId ?? 'system';
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return map;
  }, [items]);

  // Ordered list of [domainId, items] pairs
  const groups = useMemo(() => Array.from(grouped.entries()), [grouped]);

  // Wrap the items array in a fake "query" shape for QueryPage
  const wrappedQuery = {
    isPending: sotQuery.isPending || domainsQuery.isPending,
    isError: sotQuery.isError || domainsQuery.isError,
    error: sotQuery.error || domainsQuery.error,
    data: groups.length > 0 ? groups : [],
  };

  return (
    <RorosPage
      title={t('Source of Truth')}
      breadcrumbs={[
        { label: t('Governance'), path: '/roros/governance' },
        { label: t('Source of Truth'), path: '/roros/governance/source-of-truth' },
      ]}
    >
      <QueryPage
        query={wrappedQuery as any}
        emptyMessage={t('No Source of Truth items yet')}
        emptyIcon={<BookOpen className="h-8 w-8 text-muted-foreground" />}
      >
        {(pairs) => (
          <div className="space-y-6" role="tree" aria-label={t('Source of Truth tree')}>
            {(pairs as [string, RorosKnowledgeItemDTO[]][]).map(([domainId, domainItems]) => {
              const domain = domains.find((d) => d.id === domainId);
              return (
                <DomainGroup
                  key={domainId}
                  domain={domain}
                  items={domainItems}
                />
              );
            })}
          </div>
        )}
      </QueryPage>
    </RorosPage>
  );
}
